import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { MembershipPlan } from '@atomfit/shared';

interface MembershipCheckoutSelection {
  gymId?: string;
  tenantName?: string;
  tenantId: string;
  plan: MembershipPlan;
  returnTo?: 'checkout' | 'membership';
}

interface MembershipCheckoutState {
  selection: MembershipCheckoutSelection | null;
  pendingPlan: MembershipPlan | null;
  stripeAccountId: string | null;
  clientSecret: string | null;
  customerSessionClientSecret: string | null;
  purchaseId: string | null;
  subscriptionId: string | null;
  setSelection: (selection: MembershipCheckoutSelection) => void;
  setPendingPlan: (plan: MembershipPlan | null) => void;
  setPayment: (payment: {
    stripeAccountId: string | null;
    clientSecret: string | null;
    customerSessionClientSecret: string | null;
    purchaseId: string | null;
    subscriptionId: string | null;
  }) => void;
  resetPayment: () => void;
  reset: () => void;
}

type PersistedMembershipCheckoutState = Pick<MembershipCheckoutState, 'selection' | 'pendingPlan'>;

const membershipCheckoutStorageKey = 'atomfit-membership-checkout';

export const useMembershipCheckoutStore = create<MembershipCheckoutState>()(
  persist<MembershipCheckoutState, [], [], PersistedMembershipCheckoutState>(
    (set) => ({
      selection: null,
      pendingPlan: null,
      stripeAccountId: null,
      clientSecret: null,
      customerSessionClientSecret: null,
      purchaseId: null,
      subscriptionId: null,
      setSelection: (selection) =>
        set({
          selection,
          pendingPlan: selection.returnTo === 'membership' ? selection.plan : null,
          stripeAccountId: null,
          clientSecret: null,
          customerSessionClientSecret: null,
          purchaseId: null,
          subscriptionId: null,
        }),
      setPendingPlan: (pendingPlan) =>
        set({
          pendingPlan,
          selection: null,
          stripeAccountId: null,
          clientSecret: null,
          customerSessionClientSecret: null,
          purchaseId: null,
          subscriptionId: null,
        }),
      setPayment: ({
        stripeAccountId,
        clientSecret,
        customerSessionClientSecret,
        subscriptionId,
        purchaseId,
      }) =>
        set({
          stripeAccountId,
          clientSecret,
          customerSessionClientSecret,
          subscriptionId,
          purchaseId,
        }),
      resetPayment: () =>
        set({
          stripeAccountId: null,
          clientSecret: null,
          customerSessionClientSecret: null,
          purchaseId: null,
          subscriptionId: null,
        }),
      reset: () =>
        set({
          selection: null,
          pendingPlan: null,
          stripeAccountId: null,
          clientSecret: null,
          customerSessionClientSecret: null,
          purchaseId: null,
          subscriptionId: null,
        }),
    }),
    {
      name: membershipCheckoutStorageKey,
      storage: createJSONStorage<PersistedMembershipCheckoutState>(() => localStorage),
      version: 2,
      migrate: (persistedState) => {
        const state = (persistedState ?? {}) as Partial<MembershipCheckoutState>;
        return {
          selection: state.selection ?? null,
          pendingPlan: state.pendingPlan ?? null,
        };
      },
      partialize: (state) => ({
        selection: state.selection,
        pendingPlan: state.pendingPlan,
      }),
    },
  ),
);
